"use client";

import type {
  ImageContent,
  PlaceholderValues,
  Rating,
  StepOneHero,
} from "@homeinstead/schemas";
import type { FormStep, StepTransitionWrapperProps } from "./form-types";

import { ButtonStackHeroStep } from "./ButtonStackHero/ButtonStackHeroStep";
import { findButtonStackStep } from "./ButtonStackHero/map-button-stack-step";
import { LeadFormInterstitialStepItem } from "./LeadFormInterstitialStepItem";
import { LeadFormStepItem } from "./LeadFormStepItem";
import { LeadFormStepper } from "./LeadFormStepper";
import { LeadFormStepWrapper } from "./LeadFormStepWrapper";
import { LeadFormSubmissionError } from "./LeadFormSubmissionError";

type StepperProps = {
  currentStep: number;
  totalSteps: number;
  handleBack: () => void;
  showBackButton: boolean;
  showStepper: boolean;
};

type StepRegionProps = {
  currentStep: FormStep;
  errors: Record<string, string[]>;
  hasServerError: boolean;
  /**
   * Rendered between the step grid and the submission error. Keeps DOM order
   * byte-identical to the pre-extraction layout, where the mobile hero image
   * sits between the two — see LeadForm.tsx's `heroFirstStepPanel` comment.
   */
  heroSlot?: React.ReactNode;
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
  isHeroMode: boolean;
  isLastStep: boolean;
  /**
   * When variant is buttonStack, the first regular step that contains a radio
   * renders ButtonStackHeroStep and the stepper stays hidden on that step.
   */
  stepOneHero?: StepOneHero | null;
  isPending: boolean;
  officeRating?: Omit<Rating, "_type">;
  onContinue: () => void;
  onFieldChange: (name: string, value: string) => void;
  onPostalLocationChange: (values: {
    postalCode: string;
    location: string;
  }) => void;
  onValidationError: (errors: Record<string, string[]>) => void;
  persistentHeroMinHeightClass?: string;
  phoneNumber?: string;
  placeholderValues?: PlaceholderValues;
  postalLocationValues: { postalCode: string; location: string };
  steps: FormStep[];
  stepperProps: StepperProps;
  stepTransitionWrapperProps: StepTransitionWrapperProps;
  /**
   * Analytics `user_selection` per regular step, keyed by step id. Passed as a
   * Map (not pre-resolved per step) so steps.map() can call `.get(step.id)`
   * without allocating a new object on every render — see LeadForm.tsx's
   * `stepUserSelections` comment for why that matters to
   * `memo(LeadFormStepItemInner)`.
   */
  stepUserSelections: Map<
    string,
    Record<string, string | string[]> | undefined
  >;
};

/**
 * Renders, in order: stepper → step grid (all steps) → heroSlot → submission
 * error. Extracted from LeadForm.tsx as a pure move so the fullscreen
 * takeover (a later ticket) can conditionally wrap this whole region in a
 * Dialog without touching its internals.
 */
function StepRegion({
  currentStep,
  errors,
  hasServerError,
  heroSlot,
  heroImageLarge,
  heroImageSmall,
  isHeroMode,
  isLastStep,
  stepOneHero,
  isPending,
  officeRating,
  onContinue,
  onFieldChange,
  onPostalLocationChange,
  onValidationError,
  persistentHeroMinHeightClass,
  phoneNumber,
  placeholderValues,
  postalLocationValues,
  steps,
  stepperProps,
  stepTransitionWrapperProps,
  stepUserSelections,
}: StepRegionProps) {
  const buttonStackStep =
    stepOneHero?.variant === "buttonStack"
      ? findButtonStackStep(steps)
      : undefined;
  const isButtonStackActive = buttonStackStep?.id === currentStep.id;

  return (
    <>
      {/* LeadFormStepper decides which stepper variant to render: the classic
          progress-bar+label+back-button, or the animated progress bar for
          interstitial steps configured with variant="progress". The classic
          variant uses invisible to reserve its layout height when hidden so
          steps below it don't shift. Hero mode and the button-stack step omit
          it entirely so it does not reserve that space. */}
      {!isHeroMode && !isButtonStackActive ? (
        <LeadFormStepper activeStep={currentStep} stepperProps={stepperProps} />
      ) : null}

      <LeadFormStepWrapper
        isHeroMode={isHeroMode}
        persistentHeroMinHeightClass={persistentHeroMinHeightClass}
        stepTransitionWrapperProps={stepTransitionWrapperProps}
      >
        {steps.map((step) =>
          step._type === "interstitialStep" ? (
            <LeadFormInterstitialStepItem
              isActive={step.id === currentStep.id}
              isPending={isPending}
              key={step.id}
              officeRating={officeRating}
              onContinue={onContinue}
              phoneNumber={phoneNumber}
              placeholderValues={placeholderValues}
              step={step}
            />
          ) : step.id === buttonStackStep?.id ? (
            <ButtonStackHeroStep
              errors={errors}
              heroImageLarge={heroImageLarge}
              heroImageSmall={heroImageSmall}
              isActive={step.id === currentStep.id}
              isLastStep={isLastStep}
              isPending={isPending}
              isStepWrapperHeroMode={isHeroMode}
              key={step.id}
              officeRating={officeRating}
              onContinue={onContinue}
              onFieldChange={onFieldChange}
              onPostalLocationChange={onPostalLocationChange}
              onValidationError={onValidationError}
              phoneNumber={phoneNumber}
              placeholderValues={placeholderValues}
              postalLocationValues={postalLocationValues}
              step={step}
              stepUserSelection={stepUserSelections.get(step.id)}
            />
          ) : (
            <LeadFormStepItem
              errors={errors}
              heroImageLarge={heroImageLarge}
              heroImageSmall={heroImageSmall}
              isActive={step.id === currentStep.id}
              isLastStep={isLastStep}
              isPending={isPending}
              isStepWrapperHeroMode={isHeroMode}
              key={step.id}
              officeRating={officeRating}
              onContinue={onContinue}
              onFieldChange={onFieldChange}
              onPostalLocationChange={onPostalLocationChange}
              onValidationError={onValidationError}
              phoneNumber={phoneNumber}
              placeholderValues={placeholderValues}
              postalLocationValues={postalLocationValues}
              step={step}
              stepperProps={stepperProps}
              stepUserSelection={stepUserSelections.get(step.id)}
            />
          ),
        )}
      </LeadFormStepWrapper>

      {/* The button-stack shell already includes the step image. The persistent
          mobile hero sits below the step grid, so showing both paints two
          images on that step. Later steps still receive the slot once the
          shell unmounts. */}
      {isButtonStackActive ? null : heroSlot}

      {hasServerError ? <LeadFormSubmissionError /> : null}
    </>
  );
}

export { StepRegion };
